export default function RealtimeControls({ 
  isListening, 
  transcript,
  isConnecting,
  onToggleListening, 
  onStartVideo, 
  onEndSession,
  onImageUpload 
}) {
  // Handle file input for image upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Convert file to base64
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64 = event.target?.result?.split(',')[1] // Remove data:image/...;base64, prefix
      if (base64 && onImageUpload) {
        onImageUpload(base64)
      }
    }
    reader.readAsDataURL(file)
  }

  // Animated listening text with sequenced dots
  const ListeningText = () => {
    if (isConnecting) {
      return <span className="text-blue-500 animate-pulse">Connecting to AI...</span>
    }
    
    if (!isListening) {
      return <span className="text-gray-500">Ask anything</span>
    }
    
    return (
      <div className="flex items-center gap-1">
        <span className="text-gray-900">Listening</span>
        <div className="flex gap-0">
          <span className="text-gray-900 animate-pulse">.</span>
          <span className="text-gray-900 animate-pulse" style={{animationDelay: '0.3s'}}>.</span>
          <span className="text-gray-900 animate-pulse" style={{animationDelay: '0.6s'}}>.</span>
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-3" style={{
      '--dot1-animation': 'cumulativeDot1 2s infinite',
      '--dot2-animation': 'cumulativeDot2 2s infinite', 
      '--dot3-animation': 'cumulativeDot3 2s infinite'
    }}>
      {/* + Button - Upload image during conversation */}
      <input 
        type="file" 
        accept="image/*" 
        onChange={handleFileSelect}
        id="realtime-image-upload"
        className="hidden"
      />
      <label 
        htmlFor="realtime-image-upload"
        className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </label>

      {/* Status Field - Clean rounded box with animated text */}
      <div className="flex-1 relative flex items-center justify-center bg-gray-100 rounded-full px-4 py-2">
        <div className="text-sm">
          <ListeningText />
        </div>
      </div>

      {/* Inspection Mode Button - Start live video AI analysis */}
      <button 
        onClick={onStartVideo}
        className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors"
        title="Start Inspection Mode"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          {/* Simple video camera icon */}
          <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
        </svg>
      </button>

      {/* Microphone Button - Outside the listening box, shows muted when not listening */}
      <button 
        onClick={() => {
          if (!isConnecting) {
            console.log('Mic button clicked, current isListening:', isListening)
            onToggleListening()
          }
        }}
        disabled={isConnecting}
        className={`w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors ${isListening ? 'animate-pulse' : ''} ${isConnecting ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isListening ? (
          // Active microphone
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
          </svg>
        ) : (
          // Muted microphone with slash
          <div className="relative">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
            </svg>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="absolute top-0 left-0">
              <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
        )}
      </button>

      {/* End Button - Same style as realtime button in ChatInput */}
      <button 
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onEndSession()
        }}
        className="px-4 py-2 bg-blue-500 text-white rounded-full font-medium text-sm hover:bg-blue-600 transition-colors flex items-center gap-2"
      >
        {/* Animated dots showing connection status */}
        <div className="flex gap-1">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse"></div>
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
        </div>
        End
      </button>
    </div>
  )
}
